/**
 * Carte des clubs d'essai (snippets/product-club-map.liquid).
 *
 * Les points sont des boutons HTML posés en pourcentage sur la carte (taille fixe à l'écran) ; leurs
 * coordonnées dans le repère du SVG sont en data-x / data-y pour la recherche du canton.
 *
 * Un point sur la carte et une ligne de la liste parlent du même club : toucher l'un met l'autre en
 * avant et ouvre une petite fiche sur la carte (nom, lieu, site, itinéraire), comme Google Maps. Le
 * canton de chaque club est trouvé par géométrie (isPointInFill sur les tracés du snippet
 * carte-suisse) et affiché dans la liste avec ses armoiries (assets canton-xx.svg, dont les
 * adresses sont dans data-cantons). Aucun défilement de la page n'est commandé.
 */
function initialiser(root) {
  if (root.dataset.pdlCarteReady) return;
  root.dataset.pdlCarteReady = '1';

  const map = root.querySelector('[data-pdl-carte-map]');
  const pins = Array.from(root.querySelectorAll('[data-pdl-club-pin]'));
  const items = Array.from(root.querySelectorAll('[data-pdl-club]'));
  const cantons = Array.from(root.querySelectorAll('.pdl-carte__cantons path'));
  const pop = root.querySelector('[data-pdl-pop]');
  let infosCantons = {};
  try {
    infosCantons = JSON.parse(root.dataset.cantons || '{}');
  } catch {
    infosCantons = {};
  }

  /* ---- canton de chaque club (par géométrie) ---- */
  const cantonDe = new Map();
  const svgCantons = root.querySelector('.pdl-carte__svg');
  if (svgCantons && typeof SVGGeometryElement !== 'undefined' && SVGGeometryElement.prototype.isPointInFill) {
    for (const pin of pins) {
      if (!pin.dataset.x || !pin.dataset.y) continue;
      const pt = svgCantons.createSVGPoint();
      pt.x = Number(pin.dataset.x);
      pt.y = Number(pin.dataset.y);
      const canton = cantons.find((p) => {
        try {
          return p.isPointInFill(pt);
        } catch {
          return false;
        }
      });
      if (!canton) continue;
      const info = infosCantons[canton.dataset.canton];
      if (info) cantonDe.set(pin.dataset.pdlClubPin, info);
    }
  }

  const poserBadge = (el, info) => {
    if (!el || !info) return;
    const img = el.querySelector('img');
    const b = el.querySelector('b');
    if (b) b.textContent = info.a;
    if (img && info.u) {
      img.src = info.u;
      img.hidden = false;
    }
    el.hidden = false;
  };
  for (const item of items) poserBadge(item.querySelector('[data-pdl-canton]'), cantonDe.get(item.dataset.pdlClub));

  /* ---- fiche sur la carte ---- */
  let actif = null;

  const fermerFiche = () => {
    if (pop) pop.hidden = true;
  };

  const placerFiche = (pin) => {
    if (!pop || !map) return;
    const mr = map.getBoundingClientRect();
    const dot = pin.querySelector('.pdl-carte__dot') || pin;
    const pr = dot.getBoundingClientRect();
    const cx = pr.left + pr.width / 2 - mr.left;
    const cy = pr.top + pr.height / 2 - mr.top;
    pop.hidden = false;
    const pw = pop.offsetWidth;
    const ph = pop.offsetHeight;
    const marge = 8;
    let left = Math.round(cx - pw / 2);
    left = Math.max(marge, Math.min(left, mr.width - pw - marge));
    let top = Math.round(cy - ph - 16);
    const dessous = top < 0;
    if (dessous) top = Math.round(cy + 16);
    // la fiche reste dans la carte : jamais sur la liste des clubs en dessous
    top = Math.max(marge, Math.min(top, Math.round(mr.height - ph - marge)));
    pop.classList.toggle('is-below', dessous);
    pop.style.left = `${left}px`;
    pop.style.top = `${top}px`;
    pop.style.setProperty('--caret-x', `${Math.round(cx - left)}px`);
  };

  const remplirFiche = (pin) => {
    if (!pop) return;
    const nom = pop.querySelector('[data-pop-nom]');
    const lieu = pop.querySelector('[data-pop-lieu]');
    const site = pop.querySelector('[data-pop-site]');
    const maps = pop.querySelector('[data-pop-maps]');
    if (nom) nom.textContent = pin.dataset.nom || '';
    if (lieu) lieu.textContent = pin.dataset.lieu || '';
    if (site) {
      site.hidden = !pin.dataset.site;
      if (pin.dataset.site) site.href = pin.dataset.site;
    }
    if (maps) maps.href = pin.dataset.maps || '#';
    poserBadge(pop.querySelector('[data-pop-canton]'), cantonDe.get(pin.dataset.pdlClubPin));
  };

  const activer = (index) => {
    actif = index;
    for (const pin of pins) pin.classList.toggle('is-active', pin.dataset.pdlClubPin === index);
    for (const item of items) item.classList.toggle('is-active', item.dataset.pdlClub === index);
    const pin = pins.find((p) => p.dataset.pdlClubPin === index);
    if (!pin) return;
    remplirFiche(pin);
    placerFiche(pin);
    // la ligne correspondante reste visible dans la liste (conteneur défilant, jamais la page)
    const item = items.find((i) => i.dataset.pdlClub === index);
    const liste = item && item.parentElement;
    if (item && liste && liste.scrollHeight > liste.clientHeight) {
      liste.scrollTo({ top: item.offsetTop - liste.offsetTop - 6, behavior: 'smooth' });
    }
  };

  for (const pin of pins) {
    pin.addEventListener('click', (e) => {
      e.stopPropagation();
      activer(pin.dataset.pdlClubPin);
    });
  }
  for (const btn of root.querySelectorAll('[data-pdl-club-btn]')) {
    btn.addEventListener('click', () => activer(btn.dataset.pdlClubBtn));
  }
  if (pop) {
    pop.addEventListener('click', (e) => e.stopPropagation());
    pop.querySelector('[data-pdl-pop-close]')?.addEventListener('click', () => {
      fermerFiche();
      for (const pin of pins) pin.classList.remove('is-active');
      for (const item of items) item.classList.remove('is-active');
      actif = null;
    });
  }
  // un appui ailleurs sur la carte ferme la fiche ; Échap aussi
  if (map) map.addEventListener('click', fermerFiche);
  root.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') fermerFiche();
  });
  // la fiche suit le point si la carte change de taille
  if ('ResizeObserver' in window && map) {
    new ResizeObserver(() => {
      if (actif !== null && pop && !pop.hidden) {
        const pin = pins.find((p) => p.dataset.pdlClubPin === actif);
        if (pin) placerFiche(pin);
      }
    }).observe(map);
  }

  // le premier club est mis en avant à l'arrivée, fiche ouverte : on voit tout de suite où il est
  if (pins.length) activer(pins[0].dataset.pdlClubPin);
}

const demarrer = () => document.querySelectorAll('[data-pdl-carte]').forEach(initialiser);
demarrer();
document.addEventListener('shopify:section:load', demarrer);
